import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { Button } from 'components/Button';
import {
  authStrings,
  commonStrings,
  organizationStrings,
} from 'constants/strings';
import { orgPath, routes } from 'constants/routes';
import { useAuth } from 'contexts/AuthContext';
import { useMe } from 'hooks/useMe';
import { signOutCompletely } from 'lib/signOut';
import { runLegacyOrganizationStorageMigration } from 'utils/legacyStorageMigration';
import { readLastOrganizationId } from 'utils/organizationStorage';

/**
 * '/' のエントリールーティング。
 * 所属1件→その組織の/line、複数→直近選択 or 選択画面、0件→案内。
 */
export default function EntryRedirect() {
  const { user } = useAuth();
  const { data: me, isLoading, isError, refetch } = useMe();
  const location = useLocation();

  const uid = user?.uid;
  useEffect(() => {
    if (uid && me) {
      runLegacyOrganizationStorageMigration(uid, me.memberships);
    }
  }, [uid, me]);

  // 背景再取得の失敗（isError）はキャッシュ済みの me がある限り遷移を続ける
  if (isError && !me) {
    return (
      <div
        role="alert"
        className="grid place-items-center gap-3 p-6 text-sm text-danger"
      >
        <p>{organizationStrings.entryFailed}</p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => refetch()}
        >
          {commonStrings.actions.retry}
        </Button>
      </div>
    );
  }

  if (isLoading || !me) {
    return <p className="p-6 text-sm text-muted">{commonStrings.loadingEllipsis}</p>;
  }

  const { memberships } = me;

  if (memberships.length === 0) {
    return (
      <div className="grid place-items-center gap-3 p-6 text-sm text-muted">
        <p>{organizationStrings.noMemberships}</p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => void signOutCompletely()}
        >
          {authStrings.signOut}
        </Button>
      </div>
    );
  }

  if (memberships.length === 1) {
    return (
      <Navigate
        to={orgPath(memberships[0].organizationId, routes.LINE)}
        replace
      />
    );
  }

  const lastOrganizationId = uid ? readLastOrganizationId(uid) : null;
  const lastMembership = memberships.find(
    (membership) => membership.organizationId === lastOrganizationId,
  );
  if (lastMembership) {
    return (
      <Navigate
        to={orgPath(lastMembership.organizationId, routes.LINE)}
        replace
      />
    );
  }

  return (
    <Navigate to={routes.SELECT_ORGANIZATION} replace state={location.state} />
  );
}
